import React, { useState, useEffect, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import OverviewIcon from '../../../assets/icons/OverviewIcon';
import NotesIcon from '../../../assets/icons/NotesIcon';
import PreferenceIcon from '../../../assets/icons/PreferenceIcon';
import TaskIcon from '../../../assets/icons/TaskIcon';

import {
  SurgeonInfo,
  SurgeonNotes,
  SurgeonPreferences,
  SurgeonTasks,
  SurgeonPipeline,
} from '../../../components/surgeons/surgeon-page';
import LoadScreen from '../../../components/load-screen';
import NoteForm from '../../../components/customers/forms/NoteForm';
import Modal, { ConfirmationModal } from '../../../components/shared/modal';
import PreferenceForm from '../../../components/customers/forms/PreferenceForm';
import EditContactModal from '../../../components/customers/surgeons/EditContactModal';

import {
  createPreference,
  createSurgeonNote,
  deletePreference, deleteSurgeonNote,
  getQualifications,
  getSurgeon,
  getSurgeonNotes,
  getSurgeonPreferences,
  getSurgeonTasks,
  updatePreference,
  updateSurgeon,
  updateSurgeonNote,
} from '../../../actions/surgeonsActions';
import { getProcedures } from '../../../actions/proceduresActions';
import { getHospitals } from '../../../actions/hospitalsActions';
import { getPipelines, getPipelineStages } from '../../../actions/salesActions';
import { getSurgeonLeads } from '../../../actions/leadsActions';

import { routes } from '../../../constants';

import './surgeon-page.scss';

const viewOptions = {
  overview: { label: 'Overview', value: 'OVERVIEW', icon: <OverviewIcon color="var(--app-text)" /> },
  notes: { label: 'Notes', value: 'NOTES', icon: <NotesIcon color="var(--app-text)" /> },
  preferences: { label: 'Preferences', value: 'PREFERENCES', icon: <PreferenceIcon color="var(--app-text)" /> },
  tasks: { label: 'Tasks', value: 'TASKS', icon: <TaskIcon color="var(--app-text)" /> },
};

const SurgeonPage = () => {
  const history = useHistory();
  const { id } = useParams();
  const location = useLocation();
  const { search } = location;

  const queryParams = new URLSearchParams(search);

  const leadId = queryParams.get('leadId');
  const defaultView = queryParams.get('view');

  const surgeonId = id.replace(':', '');

  const dispatch = useDispatch();

  const qualifications = useSelector((state) => state?.surgeons.qualifications);
  const procedures = useSelector((state) => state.procedures.list);
  const hospitals = useSelector((state) => state.hospitals.list);
  const users = useSelector((state) => state.users.list);
  const pipelines = useSelector((state) => state.pipelines?.list || []);
  const currentUser = useSelector((state) => state.user.currentUser);
  const tenantColor = useSelector((state) => state?.tenant.currentTenant.colorPrimary);

  const [view, setActiveView] = useState(viewOptions.overview);
  const [fetching, setFetching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);

  const [surgeon, setSurgeon] = useState(null);
  const [notes, setNotes] = useState([]);
  const [preferences, setPreferences] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedPreference, setSelectedPreference] = useState(null);

  const [noteModalOpen, toggleNoteModal] = useState(false);
  const [preferenceModalOpen, togglePreferenceModal] = useState(false);
  const [editContactModal, toggleEditContactModal] = useState(false);

  const [allStages, setAllStages] = useState([]);
  const [leads, setLeads] = useState([]);

  const [selectedNoteImage, setSelectedNoteImage] = useState(null);
  const [deletingNote, setDeletingNote] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);

  const [isDeleteModalOpen, toggleDeleteModal] = useState(false);
  const [isEditModalOpen, toggleEditModal] = useState(false);
  const [isDeleteImageModalOpen, toggleDeleteImageModal] = useState(false);

  const updateParam = (key, value) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set(key, value);
    history.push({ search: searchParams.toString() });
  };

  const setView = (view) => {
    setActiveView(view);
    updateParam('view', view);
  };

  useEffect(() => {
    setView(defaultView || viewOptions.overview.value);
  }, [defaultView]);

  useEffect(() => {
    onLoad().catch();
  }, []);

  const onLoad = async () => {
    try {
      setFetching(true);

      const res = await dispatch(getSurgeon(surgeonId));
      if (res) {
        setSurgeon(res);
      }

      const pipelinesRes = await dispatch(getPipelines());
      if (pipelinesRes && pipelinesRes.length) {
        const stages = await fetchAllStages(pipelinesRes?.map((p) => p.id));
        setAllStages(stages);
      }

      await fetchData(surgeonId);
      await fetchLeads(surgeonId);

      if (!procedures?.length || !hospitals.length || !qualifications.length) {
        await Promise.all([
          dispatch(getProcedures()),
          dispatch(getHospitals()),
          dispatch(getQualifications()),
        ]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setFetching(false);
    }
  };

  const fetchData = async (surgeonId) => {
    const surgeonPreferences = await dispatch(getSurgeonPreferences(surgeonId));
    const surgeonNotes = await dispatch(getSurgeonNotes(surgeonId));
    const surgeonTasks = await dispatch(getSurgeonTasks(surgeonId));
    setPreferences(surgeonPreferences || []);
    setNotes(surgeonNotes || []);
    setTasks(surgeonTasks || []);
  };

  const fetchLeads = async (surgeonId) => {
    const res = await dispatch(getSurgeonLeads(surgeonId));
    if (res && res.length) {
      setLeads(res);
    }
  };

  const fetchAllStages = async (pipelineIds) => {
    try {
      let arr = [];
      for (const pipelineId of pipelineIds) {
        const res = await dispatch(getPipelineStages(pipelineId));
        if (res) {
          arr = [...arr, ...res];
        }
      }
      setAllStages(arr);
      return arr;
    } catch (err) {
      console.error(err);
    }
  };

  const onBackClick = () => {
    if (leadId) {
      history.push(`${routes.LEADS}/${leadId}`);
    } else {
      history.push(routes.TOOLS_SURGEONS);
    }
  };

  const onSubmitPreference = async (formObj, image) => {
    try {
      setLoading(true);
      const preferenceObj = {
        surgeonId: surgeon.id,
        procedureId: formObj.procedureId,
        hospitalId: formObj.hospitalId,
        hospitals: formObj.hospitals || [],
        preference: formObj.preference,
        links: formObj.links ? formObj.links.filter((v) => !!v) : [],
      };

      if (selectedPreference) {
        await dispatch(updatePreference(surgeon.id, selectedPreference.id, preferenceObj, image));
      } else {
        await dispatch(createPreference(surgeon.id, preferenceObj, image));
      }

      await fetchData(surgeon.id);

      setLoading(false);
      togglePreferenceModal(false);
      setSelectedPreference(null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onCreateNote = async (formObj) => {
    const noteData = {
      note: formObj.note,
    };

    setLoading(true);

    try {
      dispatch(createSurgeonNote(surgeon.id, noteData));
      const surgeonNotes = await dispatch(getSurgeonNotes(surgeon.id));
      setNotes(surgeonNotes || []);
      toggleNoteModal(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onDeleteImageClick = async (image) => {
    setSelectedNoteImage(image);
    toggleDeleteImageModal(true);
  };

  const onDeleteImage = async () => {
    if (selectedNote && selectedNote.id && selectedNoteImage) {
      const noteData = {
        note: selectedNote.note,
        attachments: selectedNote?.attachments ? selectedNote?.attachments?.filter((item) => item.fileName !== selectedNoteImage.fileName) : [],
      };

      setDeletingNote(true);

      try {
        await dispatch(updateSurgeonNote(surgeon?.id, selectedNote.id, noteData, []));
        setSelectedNote({ ...selectedNote, attachments: noteData?.attachments });
        const surgeonNotes = await dispatch(getSurgeonNotes(surgeon.id));
        setNotes(surgeonNotes || []);
        toggleDeleteImageModal(false);
      } catch (err) {
        console.error(err);
      } finally {
        setDeletingNote(false);
      }
    }
  };

  const onEditClick = (note) => {
    setSelectedNote(note);
    toggleEditModal(true);
  }

  const onUpdate = async (formObj, newFiles) => {
    if (selectedNote && selectedNote.id) {
      const noteData = {
        note: formObj.note,
        attachments: selectedNote?.attachments || []
      };

      setLoading(true);

      try {
        await dispatch(updateSurgeonNote(surgeon?.id, selectedNote.id, noteData, newFiles));
        const surgeonNotes = await dispatch(getSurgeonNotes(surgeon.id));
        setNotes(surgeonNotes || []);
        toggleEditModal(false);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const onDeleteClick = (note) => {
    setSelectedNote(note);
    toggleDeleteModal(true);
  }

  const onDelete = async () => {
    if (selectedNote && selectedNote.id) {
      try {
        setDeletingNote(true);
        await dispatch(deleteSurgeonNote(surgeon?.id, selectedNote.id));
        const surgeonNotes = await dispatch(getSurgeonNotes(surgeon.id));
        setNotes(surgeonNotes || []);
        toggleDeleteModal(false);
      } catch (err) {
        console.error(err);
      } finally {
        setDeletingNote(false);
      }
    }
  };

  const onDeletePreference = async () => {
    try {
      setDeleting(true);
      await dispatch(deletePreference(surgeon.id, selectedPreference.id));
      await fetchData(surgeon.id);
      setSelectedPreference(null);
    } finally {
      setDeleting(false);
    }
  };

  const onContactUpdate = async (formObj) => {
    try {
      setEditing(true);
      const surgeonData = {
        email: formObj?.email || '',
        phone: formObj?.phone || '',
        linkedin: formObj?.linkedin || '',
        website: formObj?.website || '',
      };

      await dispatch(updateSurgeon(surgeon.id, surgeonData));
      setSurgeon({
        ...surgeon,
        ...surgeonData,
      });
      toggleEditContactModal(false);
    } catch (err) {
      console.error(err);
    } finally {
      setEditing(false);
    }
  };

  const onLeadClick = (leadId) => {
    history.push(`${routes.LEADS}/${leadId}?from=SURGEON`);
  };

  const renderView = () => {
    switch (view) {
      case viewOptions.notes.value:
        return (
          <SurgeonNotes
            notes={notes}
            users={users}
            onAddClick={() => toggleNoteModal(true)}
          />
        );
      case viewOptions.preferences.value:
        return (
          <SurgeonPreferences
            preferences={preferences}
            onAddClick={() => togglePreferenceModal(true)}
            procedures={procedures}
            hospitals={hospitals}
            onPreferenceClick={(preference) => {
              setSelectedPreference(preference);
              togglePreferenceModal(true);
            }}
          />
        );
      case viewOptions.tasks.value:
        return (
          <SurgeonTasks
            tasks={tasks}
          />
        )
      default:
        return null;
    }
  };

  const options = useMemo(() => Object.values(viewOptions), [viewOptions]);

  return (
    <>
      {fetching && <LoadScreen />}
      <div className="surgeon-page__container">
        <div>
          <SurgeonInfo
            surgeon={surgeon}
            view={view}
            setView={setView}
            options={options}
            hospitals={hospitals}
            procedures={procedures}
            users={users}
            qualifications={qualifications}
            onBackClick={onBackClick}
            onEditClick={() => toggleEditContactModal(true)}
          />
          <SurgeonPipeline
            leads={leads}
            pipelines={pipelines}
            stages={allStages}
            users={users}
            onLeadClick={onLeadClick}
          />
        </div>
        {view === viewOptions.overview.value ? (
          <>
            <SurgeonNotes
              notes={notes}
              users={users}
              onAddClick={() => toggleNoteModal(true)}
              onEditClick={onEditClick}
              onDeleteClick={onDeleteClick}
              tenantColor={tenantColor}
              currentUser={currentUser}
            />
            <SurgeonPreferences
              preferences={preferences}
              onAddClick={() => togglePreferenceModal(true)}
              procedures={procedures}
              hospitals={hospitals}
              onPreferenceClick={(preference) => {
                setSelectedPreference(preference);
                togglePreferenceModal(true);
              }}
            />
          </>
        ) : (
          <>
            {renderView()}
            <div />
          </>
        )}
      </div>

      <Modal
        open={preferenceModalOpen}
        onClose={() => {
          togglePreferenceModal(false)
        }}
      >
        <PreferenceForm
          initialValues={selectedPreference}
          procedures={procedures}
          hospitals={hospitals}
          onSubmit={onSubmitPreference}
          onClose={() => {
            togglePreferenceModal(false);
            setSelectedPreference(null);
          }}
          loading={loading}
          onDelete={onDeletePreference}
          deleting={deleting}
          mode={selectedPreference ? 'update' : 'create'}
        />
      </Modal>


      <Modal
        open={noteModalOpen}
        onClose={() => toggleNoteModal(false)}
      >
        <NoteForm
          loading={loading}
          onSubmit={onCreateNote}
          onClose={() => toggleNoteModal(false)}
          withoutText
          tenantColor={tenantColor}
          onDeleteImage={onDeleteImageClick}
        />
      </Modal>

      {!!selectedNote && (
        <Modal
          open={isEditModalOpen}
          onClose={() => {
            toggleEditModal(false);
            setSelectedNote(null);
          }}
        >
          <NoteForm
            mode='update'
            initialValues={selectedNote}
            loading={loading}
            onSubmit={onUpdate}
            onClose={() => toggleEditModal(false)}
            tenantColor={tenantColor}
            onDeleteImage={onDeleteImageClick}
          />
        </Modal>
      )}

      {!!surgeon && (
        <EditContactModal
          open={editContactModal}
          onClose={() => toggleEditContactModal(false)}
          initialValues={surgeon}
          onSubmit={onContactUpdate}
          loading={editing}
        />
      )}

      <ConfirmationModal
        open={isDeleteModalOpen}
        onClose={() => {
          toggleDeleteModal(false);
          setSelectedNote(null);
        }}
        onSubmit={onDelete}
        title='Are you sure you want to delete this note?'
        submitText='Delete'
        loading={deletingNote}
      />

      <ConfirmationModal
        open={isDeleteImageModalOpen}
        onClose={() => {
          toggleDeleteImageModal(false);
          setSelectedNoteImage(null);
        }}
        onSubmit={onDeleteImage}
        title='Are you sure you want to delete this attachment?'
        submitText='Delete'
        loading={deletingNote}
      />
    </>
  )
};

export default SurgeonPage;
